"use client"

import { toEthiopianTime } from "@/lib/ethiopian-date-conversion";
import { useState, useEffect } from "react"
import { useTranslation } from 'react-i18next';
import { Clock } from "lucide-react"
import { useBookingStore } from "@/store/booking-store"
import { useServerTime } from "@/utils/server-time"

type SlotItem = {
  duration_id: string
  time_from: string
  time_to: string
  is_morning: boolean
  applicable_days: number[]
  current_appointments: string
  remaining_slots: number
}

function formatTimeLabel(timeFrom: string, timeTo: string, modifier: string, calendarType?: 'GC' | 'EC'): string {
  // Parse the time (format: "HH:mm:ss" or "HH:mm")
  const [hours, minutes] = timeFrom.split(":").map(Number);
  
  // If Gregorian calendar, return standard 12-hour format
  if (calendarType === 'GC') {
    const hour12 = hours % 12 || 12;
    const amPm = hours >= 12 ? 'PM' : 'AM';
    return `${hour12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }
  
  // If Ethiopian calendar, convert to Ethiopian time
  const time12h = `${hours % 12 || 12}:${minutes.toString().padStart(2, '0')}`;
  const etTimeFrom = toEthiopianTime(time12h, modifier)
  return `${etTimeFrom.slice(0,5)}`
}

export default function SlotsGrid({ slots, selectedDate, calendarType }: { slots: SlotItem[]; selectedDate: string; calendarType?: 'GC' | 'EC' }) {
  const { selectedSlot, setBookingState } = useBookingStore()
  const { t } = useTranslation();
  const { serverTimeMs } = useServerTime();

  const morning = slots.filter((s) => s.is_morning)
  const afternoon = slots.filter((s) => !s.is_morning)

  function renderBlock(label: string, items: SlotItem[]) {
    return (
      <div className="animate-fade-in-up">
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-2 h-2 rounded-full ${label === "morning" ? "bg-orange-400" : "bg-indigo-400"}`} />
          <div className="text-md font-bold text-gray-700 uppercase tracking-wider">{t(label)}</div>
          <div className="h-px flex-1 bg-gray-200" />
        </div>
        
        {items.length === 0 && (
          <div className="flex flex-col items-center justify-center py-3 md:py-6  text-center bg-gray-50 rounded-xl border border-dashed border-gray-200 md:mb-4">
            <div className="flex items-center justify-center gap-2 md:flex-col">
              <Clock className="w-4 h-4 md:w-8 md:h-8 text-gray-300" />
              <p className="text-sm text-gray-500 font-medium">{t("No slot available")}</p>
            </div>
            <p className="text-xs text-gray-400 mt-1">{t("Please check another time or date")}</p>
          </div>
        )}
        
        <div className="grid grid-cols-4 min-[480px]:grid-cols-5  gap-3 mb-6">
          {items.map((s, index) => {
            const enabled = (s.remaining_slots ?? 0) > 0
            const key = `${s.time_from}-${s.time_to}`
            // Check if this slot is the selected one in store
            const isSelected = selectedSlot && selectedSlot.time_from === s.time_from && selectedSlot.time_to === s.time_to
            
            return (
              <button
                key={s.duration_id || key}
                disabled={!enabled}
                style={{ animationDelay: `${index * 30}ms` }}
                onClick={() => {
                  if (!enabled) return
                  setBookingState({ selectedSlot: s, lastSlotSelectionTime: serverTimeMs > 0 ? serverTimeMs : 0 })
                }}
                className={`
                  relative group flex flex-col items-center justify-center py-3 px-1 md:px-2 rounded-xl border transition-all duration-200 animate-fade-in-up opacity-0 fill-mode-forwards
                  ${isSelected 
                    ? "bg-pink-600 border-pink-600 text-white shadow-lg shadow-pink-200 scale-105 z-10" 
                    : "bg-white border-gray-200 hover:border-pink-300 hover:shadow-md hover:-translate-y-0.5"
                  } 
                  ${enabled ? "cursor-pointer" : "opacity-40 cursor-not-allowed bg-gray-50 hover:border-gray-200 hover:shadow-none hover:translate-y-0"}
                `}
              >
                <div className={`text-md md:text-lg font-bold mb-1 whitespace-nowrap ${isSelected ? "text-white" : "text-gray-900"}`}>
                  {formatTimeLabel(s.time_from , s.time_to, label === "morning" ? "AM" : "PM", calendarType)}
                </div>
                
                <div className={`
                  text-[11.5px] font-medium px-2 py-0.5 rounded-full transition-colors
                  ${isSelected 
                    ? "bg-white/20 text-white" 
                    : enabled ? "bg-green-50 text-green-700 group-hover:bg-pink-50 group-hover:text-pink-700" : "bg-gray-100 text-gray-400"
                  }
                `}>
                  {s.remaining_slots} {t("slots")}
                </div>
                
                {isSelected && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-white rounded-full border-2 border-pink-600" />
                )}
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="mt-6 md:px-2">
      {renderBlock("morning", morning)}
      {renderBlock("afternoon", afternoon)}
    </div>
  )
}


